import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileText, Printer } from 'lucide-react';
import {
  formatIqd,
  fromMillidinars,
  type ReportCellValue,
  type ReportColumn,
  type ReportRow,
  type ReportTableDto,
} from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import { downloadReportExport, type ReportRangeState } from './useReports';

interface ReportTableViewProps {
  report: ReportTableDto;
  range: ReportRangeState;
  /** The category filter the table was fetched with, so an export matches it. */
  categoryIds?: readonly string[];
}

/**
 * One renderer for every report (SPEC §5.7).
 *
 * Reports arrive as typed columns plus rows, so the 20-plus named reports share
 * this component and the same two export paths. A per-report component would
 * mean 20 places for a money column to be formatted as a float.
 */
export function ReportTableView({ report, range, categoryIds }: ReportTableViewProps) {
  const { t, i18n } = useTranslation();
  const { can } = useAuth();
  const [exporting, setExporting] = useState<null | 'csv' | 'pdf'>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const canExport = can('reports.export');

  async function handleExport(format: 'csv' | 'pdf'): Promise<void> {
    setExporting(format);
    setExportError(null);
    try {
      await downloadReportExport(`/reports/${report.id}/export`, range, format, categoryIds);
    } catch (caught) {
      setExportError(caught instanceof Error ? caught.message : 'Export failed.');
    } finally {
      setExporting(null);
    }
  }

  const format = (value: ReportCellValue, column: ReportColumn): string => {
    if (value === null || value === undefined || value === '') return '—';

    switch (column.type) {
      case 'money':
        // Money crosses the wire as a millidinar STRING and is formatted from
        // the exact value — never parsed to a float on the way to the screen.
        return typeof value === 'string' && /^[+-]?\d+$/.test(value)
          ? formatIqd(fromMillidinars(value), {
              currencySymbol: report.currencySymbol,
              locale: i18n.language,
            })
          : '—';
      case 'percent':
        return typeof value === 'number' ? `${value}%` : String(value);
      case 'boolean':
        return value ? t('reports.yes') : t('reports.no');
      case 'number':
        return new Intl.NumberFormat(i18n.language, { numberingSystem: 'latn' }).format(
          typeof value === 'number' ? value : Number(value) || 0,
        );
      case 'date':
        return String(value);
      case 'datetime': {
        const parsed = new Date(String(value));
        return Number.isNaN(parsed.getTime())
          ? String(value)
          : new Intl.DateTimeFormat(i18n.language, {
              dateStyle: 'short',
              timeStyle: 'short',
              numberingSystem: 'latn',
            }).format(parsed);
      }
      default:
        return String(value);
    }
  };

  const isNumeric = (column: ReportColumn) =>
    column.type === 'money' || column.type === 'number' || column.type === 'percent';

  return (
    <section className="gc-report-print flex min-h-0 flex-1 flex-col">
      <header className="gc-no-print flex flex-wrap items-center gap-3 pb-3">
        <div>
          <h2 className="text-lg font-semibold text-content">{t(report.titleKey, report.title)}</h2>
          <p className="text-xs text-content-muted">
            {t('reports.rowCount', { count: report.rowCount })}
          </p>
        </div>

        <div className="flex-1" />

        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 rounded-full border border-subtle px-3 py-1.5 text-sm text-content-secondary hover:bg-hover"
        >
          <Printer className="h-4 w-4" aria-hidden />
          {t('common.print')}
        </button>

        <button
          type="button"
          onClick={() => void handleExport('csv')}
          disabled={!canExport || exporting !== null}
          title={canExport ? undefined : t('reports.noExportPermission')}
          className="inline-flex items-center gap-2 rounded-full border border-subtle px-3 py-1.5 text-sm text-content-secondary hover:bg-hover disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Download className="h-4 w-4" aria-hidden />
          {exporting === 'csv' ? t('common.loading') : t('reports.exportCsv')}
        </button>

        <button
          type="button"
          onClick={() => void handleExport('pdf')}
          disabled={!canExport || exporting !== null}
          title={canExport ? undefined : t('reports.noExportPermission')}
          className="inline-flex items-center gap-2 rounded-full border border-subtle px-3 py-1.5 text-sm text-content-secondary hover:bg-hover disabled:cursor-not-allowed disabled:opacity-40"
        >
          <FileText className="h-4 w-4" aria-hidden />
          {exporting === 'pdf' ? t('common.loading') : t('reports.exportPdf')}
        </button>
      </header>

      {exportError ? (
        <p role="alert" className="gc-no-print pb-2 text-sm text-danger">
          {exportError}
        </p>
      ) : null}

      {/* Phone: a card per row — every column as a label:value line, nothing
          dropped and nothing scrolling sideways. One transform here covers all
          twenty-plus reports and the Store ledger, because they all render
          through this component. */}
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto md:hidden">
        {report.rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-content-muted">{t('common.noResults')}</p>
        ) : (
          report.rows.map((row, index) => (
            <div key={index} className="rounded-card border border-subtle bg-raised p-3">
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-sm">
                {report.columns.map((column) => (
                  <Fragment key={column.key}>
                    <dt className="text-xs leading-6 text-content-muted">
                      {t(column.labelKey, column.label)}
                    </dt>
                    <dd
                      className={`min-w-0 break-words text-end leading-6 text-content ${
                        isNumeric(column) ? 'tabular' : ''
                      }`}
                    >
                      {format(row[column.key] ?? null, column)}
                    </dd>
                  </Fragment>
                ))}
              </dl>
            </div>
          ))
        )}
        {report.totals ? (
          <div className="rounded-card border border-strong bg-raised p-3">
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-sm font-medium">
              {report.columns.map((column) => {
                const total = formatTotal(report.totals as ReportRow, column, format);
                if (!total) return null;
                return (
                  <Fragment key={column.key}>
                    <dt className="text-xs leading-6 text-content-muted">
                      {t(column.labelKey, column.label)}
                    </dt>
                    <dd className="tabular min-w-0 text-end leading-6 text-content">{total}</dd>
                  </Fragment>
                );
              })}
            </dl>
          </div>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-auto rounded-card border border-subtle max-md:hidden">
        <table className="w-full min-w-max border-collapse text-sm">
          <thead className="sticky top-0 bg-overlay">
            <tr className="border-b border-strong">
              {report.columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={`whitespace-nowrap px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-content-muted ${
                    isNumeric(column) ? 'text-end' : 'text-start'
                  }`}
                >
                  {t(column.labelKey, column.label)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {report.rows.length === 0 ? (
              <tr>
                <td
                  colSpan={report.columns.length}
                  className="px-3 py-6 text-center text-sm text-content-muted"
                >
                  {t('common.noResults')}
                </td>
              </tr>
            ) : (
              report.rows.map((row, index) => (
                <tr
                  key={index}
                  className={`border-b border-subtle/50 ${index % 2 === 1 ? 'bg-row-odd' : 'bg-row-even'}`}
                >
                  {report.columns.map((column) => (
                    <td
                      key={column.key}
                      className={`whitespace-nowrap px-3 py-2 text-content ${
                        isNumeric(column) ? 'tabular text-end' : 'text-start'
                      }`}
                    >
                      {format(row[column.key] ?? null, column)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
          {/* The totals row is what an owner scans a report FOR: raised ink,
              a strong rule above it, its figures in accent cyan. */}
          {report.totals ? (
            <tfoot className="sticky bottom-0 bg-overlay">
              <tr className="border-t border-strong font-semibold">
                {report.columns.map((column, index) => (
                  <td
                    key={column.key}
                    className={`whitespace-nowrap px-3 py-2 ${
                      isNumeric(column)
                        ? 'tabular text-end text-primary'
                        : 'text-start text-content'
                    }`}
                  >
                    {index === 0
                      ? t('common.total')
                      : formatTotal(report.totals as ReportRow, column, format)}
                  </td>
                ))}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>

      {report.note ? <p className="pt-2 text-xs text-content-muted">{report.note}</p> : null}
    </section>
  );
}

/** Only money and number columns carry a footer total; the rest stay blank. */
function formatTotal(
  totals: ReportRow,
  column: ReportColumn,
  format: (value: ReportCellValue, column: ReportColumn) => string,
): string {
  const value = totals[column.key];
  if (value === null || value === undefined) return '';
  return format(value, column);
}
